import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

export default function CoordinatorDashboard() {
  const { user, logout } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [farmerDetails, setFarmerDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCoordinatorData();
  }, []);

  const fetchCoordinatorData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch assigned farmers
      const farmersRes = await fetch('/api/coordinator/farmers', { headers });
      if (farmersRes.ok) {
        const data = await farmersRes.json();
        setFarmers(data.farmers || []);
      }

      // Fetch stats
      const statsRes = await fetch('/api/coordinator/stats', { headers });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats || {});
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFarmerDetails = async (farmerId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch farmer's kilns and burns
      const [kilnsRes, burnsRes] = await Promise.all([
        fetch(`/api/admin/kilns?farmer_id=${farmerId}`, { headers }),
        fetch(`/api/admin/burns?farmer_id=${farmerId}`, { headers })
      ]);

      const kilnsData = kilnsRes.ok ? await kilnsRes.json() : { kilns: [] };
      const burnsData = burnsRes.ok ? await burnsRes.json() : { burns: [] };

      setFarmerDetails({
        kilns: kilnsData.kilns || [],
        burns: burnsData.burns || []
      });
    } catch (err) {
      console.error('Error fetching farmer details:', err);
    }
  };

  const handleFarmerClick = (farmer) => {
    setSelectedFarmer(farmer);
    fetchFarmerDetails(farmer.farmer_id);
  };

  const closeFarmerModal = () => {
    setSelectedFarmer(null);
    setFarmerDetails(null);
  };

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1f15 0%, #1a3d2e 50%, #0d2818 100%)',
      color: '#e0e0e0',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      background: 'rgba(15, 36, 25, 0.95)',
      borderBottom: '1px solid #2d5a45',
      padding: '16px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '16px'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    logoText: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#4ade80'
    },
    tagline: {
      color: '#f87171',
      fontSize: '14px',
      fontStyle: 'italic'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    roleBadge: {
      background: '#8b5cf6',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    logoutBtn: {
      padding: '8px 16px',
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 'bold'
    },
    main: {
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    statCard: {
      background: '#0f2419',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #2d5a45',
      textAlign: 'center'
    },
    statValue: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#4ade80'
    },
    statLabel: {
      color: '#888',
      fontSize: '14px',
      marginTop: '4px'
    },
    section: {
      background: '#0f2419',
      borderRadius: '12px',
      border: '1px solid #2d5a45',
      overflow: 'hidden',
      marginBottom: '24px'
    },
    sectionHeader: {
      background: '#1a3d2e',
      padding: '16px 20px',
      borderBottom: '1px solid #2d5a45',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#4ade80',
      margin: 0
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      background: '#1a3d2e',
      color: '#4ade80',
      fontWeight: '600',
      fontSize: '13px',
      borderBottom: '1px solid #2d5a45'
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid #2d5a45',
      fontSize: '14px'
    },
    tr: {
      cursor: 'pointer',
      transition: 'background 0.2s'
    },
    statusBadge: (status) => ({
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: status === 'verified' ? '#22c55e' : status === 'ready' ? '#3b82f6' : '#f59e0b',
      color: 'white'
    }),
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: '#0f2419',
      borderRadius: '12px',
      border: '1px solid #2d5a45',
      maxWidth: '800px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalHeader: {
      padding: '20px',
      borderBottom: '1px solid #2d5a45',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      color: '#888',
      fontSize: '24px',
      cursor: 'pointer'
    },
    modalBody: {
      padding: '20px'
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: '1px solid #2d5a45',
      paddingBottom: '12px'
    },
    tab: (active) => ({
      padding: '8px 16px',
      background: active ? '#4ade80' : 'transparent',
      color: active ? '#0f2419' : '#888',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: active ? 'bold' : 'normal'
    }),
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px'
    },
    infoItem: {
      background: '#1a3d2e',
      padding: '12px',
      borderRadius: '8px'
    },
    infoLabel: {
      color: '#888',
      fontSize: '12px',
      marginBottom: '4px'
    },
    infoValue: {
      color: '#fff',
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌱</div>
          <p>Loading coordinator dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <span style={{ fontSize: '40px' }}>🌾</span>
          <div>
            <div style={styles.logoText}>AgroConnect.io</div>
            <div style={styles.tagline}>Coordinator Dashboard</div>
          </div>
        </div>
        <div style={styles.userInfo}>
          <span>{user?.email}</span>
          <span style={styles.roleBadge}>Coordinator</span>
          <button style={styles.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        {/* Welcome */}
        <h1 style={{ color: '#4ade80', marginBottom: '8px' }}>
          Welcome, Coordinator! 👋
        </h1>
        <p style={{ color: '#888', marginBottom: '32px' }}>
          Manage your assigned farmers and track their biochar activities.
        </p>

        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{farmers.length}</div>
            <div style={styles.statLabel}>Assigned Farmers</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_kilns || 0}</div>
            <div style={styles.statLabel}>Total Kilns</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.verified_kilns || 0}</div>
            <div style={styles.statLabel}>Verified Kilns</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.completed_burns || 0}</div>
            <div style={styles.statLabel}>Completed Burns</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: '#f59e0b' }}>
              {(stats.total_credits || 0).toFixed(2)}
            </div>
            <div style={styles.statLabel}>Total Credits</div>
          </div>
        </div>

        {/* Farmers Table */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>👨‍🌾 My Farmers ({farmers.length})</h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Village</th>
                  <th style={styles.th}>County</th>
                  <th style={styles.th}>Farm Size</th>
                  <th style={styles.th}>Crops</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {farmers.map((farmer, idx) => (
                  <tr 
                    key={farmer.farmer_id} 
                    style={{ 
                      ...styles.tr, 
                      background: idx % 2 === 0 ? 'transparent' : 'rgba(45, 90, 69, 0.2)'
                    }}
                    onClick={() => handleFarmerClick(farmer)}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(74, 222, 128, 0.1)'}
                    onMouseOut={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(45, 90, 69, 0.2)'}
                  >
                    <td style={styles.td}>
                      <strong style={{ color: '#4ade80' }}>{farmer.full_name || 'N/A'}</strong>
                      <br />
                      <span style={{ color: '#888', fontSize: '12px' }}>{farmer.email}</span>
                    </td>
                    <td style={styles.td}>{farmer.village || '-'}</td>
                    <td style={styles.td}>{farmer.district || farmer.county || '-'}</td>
                    <td style={styles.td}>{farmer.plot_size_ha ? `${farmer.plot_size_ha} ha` : '-'}</td>
                    <td style={{ ...styles.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {farmer.current_crops || '-'}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge(farmer.verified ? 'verified' : 'pending')}>
                        {farmer.verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>⚡ Quick Actions</h2>
          </div>
          <div style={{ padding: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <button style={{
              padding: '12px 24px',
              background: '#4ade80',
              color: '#0f2419',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📋 Export Farmer List
            </button>
            <button style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📊 Generate Report
            </button>
          </div>
        </div>
      </main>

      {/* Farmer Detail Modal */}
      {selectedFarmer && (
        <div style={styles.modal} onClick={closeFarmerModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ color: '#4ade80', margin: 0 }}>
                {selectedFarmer.full_name}
              </h2>
              <button style={styles.closeBtn} onClick={closeFarmerModal}>×</button>
            </div>
            <div style={styles.modalBody}>
              {/* Farmer Info */}
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Email</div>
                  <div style={styles.infoValue}>{selectedFarmer.email}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Village</div>
                  <div style={styles.infoValue}>{selectedFarmer.village || '-'}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>County</div>
                  <div style={styles.infoValue}>{selectedFarmer.district || '-'}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Farm Size</div>
                  <div style={styles.infoValue}>{selectedFarmer.plot_size_ha ? `${selectedFarmer.plot_size_ha} ha` : '-'}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>GPS</div>
                  <div style={styles.infoValue}>
                    {selectedFarmer.gps_lat && selectedFarmer.gps_lng 
                      ? `${selectedFarmer.gps_lat}, ${selectedFarmer.gps_lng}` 
                      : '-'}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Crops</div>
                  <div style={styles.infoValue}>{selectedFarmer.current_crops || '-'}</div>
                </div>
              </div>

              {/* Baseline Notes */}
              {selectedFarmer.farmer_notes && (
                <div style={{ marginTop: '20px', padding: '16px', background: '#1a3d2e', borderRadius: '8px' }}>
                  <div style={{ color: '#4ade80', fontWeight: 'bold', marginBottom: '8px' }}>📋 Baseline Survey</div>
                  <div style={{ color: '#ccc', fontSize: '14px', lineHeight: '1.6' }}>
                    {selectedFarmer.farmer_notes}
                  </div>
                </div>
              )}

              {/* Kilns & Burns */}
              {farmerDetails && (
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{ color: '#4ade80', marginBottom: '12px' }}>🔥 Kilns ({farmerDetails.kilns.length})</h3>
                  {farmerDetails.kilns.length > 0 ? (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {farmerDetails.kilns.map(kiln => (
                        <div key={kiln.kiln_id} style={{
                          background: '#1a3d2e',
                          padding: '12px',
                          borderRadius: '8px',
                          minWidth: '200px'
                        }}>
                          <div style={{ fontWeight: 'bold', color: '#fff' }}>{kiln.kiln_id}</div>
                          <div style={{ color: '#888', fontSize: '12px' }}>{kiln.kiln_type}</div>
                          <span style={styles.statusBadge(kiln.status)}>{kiln.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#888' }}>No kilns registered yet.</p>
                  )}

                  <h3 style={{ color: '#4ade80', marginTop: '20px', marginBottom: '12px' }}>
                    📊 Burns ({farmerDetails.burns.length})
                  </h3>
                  {farmerDetails.burns.length > 0 ? (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {farmerDetails.burns.map(burn => (
                        <div key={burn.burn_id} style={{
                          background: '#1a3d2e',
                          padding: '12px',
                          borderRadius: '8px',
                          minWidth: '200px'
                        }}>
                          <div style={{ fontWeight: 'bold', color: '#fff' }}>{burn.burn_id}</div>
                          <div style={{ color: '#888', fontSize: '12px' }}>
                            {burn.biochar_kg ? `${burn.biochar_kg} kg` : 'N/A'}
                          </div>
                          <div style={{ color: '#f59e0b', fontSize: '14px', marginTop: '4px' }}>
                            {burn.credits_earned ? `${burn.credits_earned} credits` : '-'}
                          </div>
                          <span style={styles.statusBadge(burn.status)}>{burn.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#888' }}>No burns logged yet.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
